/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from 'reactstrap';
import Dashboard, { DashboardRow } from '../../components/dashboard';
import { ROUTES } from '../../constants/routes';
import { useAppSelector } from '../../reducers';
import { AdminBreadcrumbs } from '../../routes/admin-dashboard/AdminBreadcrumbs';
import { CustomMetricRouteParams } from '../../routes/custom-metrics';
import { generateUrl } from '../../routes/util';
import G17Client from '../../services/G17Client';
import { FeaturePermissions } from '../../services/permissions/FeaturePermissions';
import { AccessType, MetricGroup } from '../../types/metricGroup';
import { getGroupLabel } from '../../utils/metricGroup';
import { BasicAlert } from '../alert/BasicAlert';
import { CustomMetricsViewMode, ViewMode } from './constants';
import { CustomMetricContext } from './CustomMetricContainer';
import CustomMetricDashboard from './CustomMetricDashboard';
import { CustomMetricManage } from './CustomMetricManage';
import { MetricConfig } from './MetricGroupForm';
import { MetricGroupFormContainer } from './MetricGroupFormContainer';
import './styles.scss';

interface MetricGroupOption {
  value: string;
  label: string;
}

interface CustomMetricProps {
  handleReload: (firstRun?: boolean) => Promise<void>;
  handleChangeMetricGroup: (params: CustomMetricRouteParams) => void;
}

const CustomMetrics = (props: CustomMetricProps) => {
  const { metricGroups, groupId, isPortfolioTracker, initiativeId, view, surveyId } =
    React.useContext(CustomMetricContext);
  const [metricConfig, setMetricConfig] = React.useState<MetricConfig>({
    count: undefined,
  });
  const history = useHistory();
  const metricLimit = useAppSelector(FeaturePermissions.getLimitCustomMetric);
  const isDashboardMode = groupId === CustomMetricsViewMode.Dashboard;
  const isNewGroupMode = groupId === CustomMetricsViewMode.Create;
  const isManageMode = groupId === CustomMetricsViewMode.Manage;

  const metricGroupOptions: MetricGroupOption[] = [];
  const assignedMetricGroupOptions: MetricGroupOption[] = [];

  const loadMetricConfig = useCallback((initiativeId: string) => {
    G17Client.getCustomMetricsConfig(initiativeId).then((config: MetricConfig) =>
      setMetricConfig({
        count: config.count,
      })
    );
  }, []);

  useEffect(() => {
    if (initiativeId) {
      loadMetricConfig(initiativeId);
    }
  }, [initiativeId, loadMetricConfig]);

  metricGroups.forEach((g: MetricGroup) => {
    const item = { value: g._id ?? '', label: getGroupLabel(g) };
    if (g.accessType === AccessType.Assigned) {
      assignedMetricGroupOptions.push(item);
    } else {
      metricGroupOptions.push(item);
    }
  });

  const handleReload = async () => {
    await props.handleReload();
    loadMetricConfig(initiativeId);
  };

  const currentMetricGroup = useMemo(() => {
    if (!isNewGroupMode) {
      return metricGroups.find((g) => g._id === groupId);
    }
  }, [isNewGroupMode, metricGroups, groupId]);

  const isAssigned = assignedMetricGroupOptions.some((o) => o.value === groupId);

  const handleClickManageCustomMetricUrl = () => {
    const rootUrl = isPortfolioTracker ? ROUTES.PORTFOLIO_TRACKER_CUSTOM_METRICS : ROUTES.CUSTOM_METRICS;
    const url = generateUrl(rootUrl, {
      initiativeId,
      groupId: CustomMetricsViewMode.Manage,
      ...(isPortfolioTracker && { portfolioId: initiativeId }),
    });
    history.push(url);
  };

  const showAlert = () => {
    const hasHitQuestionLimit = metricConfig.count && metricLimit && metricConfig.count >= metricLimit;
    if (hasHitQuestionLimit) {
      return (
        <BasicAlert type='warning' className='d-flex align-items-baseline text-ThemeWarningExtradark'>
          <span>{`${metricLimit} custom metric limit reached - you can delete some metrics or buy more here:`}</span>
          <Button
            color='link text-ThemeWarningExtradark'
            className='text-decoration-underline px-1 py-0'
            onClick={handleClickManageCustomMetricUrl}
          >
            Manage custom metrics
          </Button>
        </BasicAlert>
      );
    }
    return <></>;
  };

  const goToSurvey = () => {
    if (!surveyId) {
      return;
    }
    const url = generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, { initiativeId, surveyId, page: 'scope' });
    history.push(`${url}/custom`);
  };

  const renderBreadcrumbs = () => {
    const baseRoute = isPortfolioTracker ? ROUTES.PORTFOLIO_TRACKER_CUSTOM_METRICS : ROUTES.CUSTOM_METRICS;
    const breadcrumbs = [
      {
        label: 'Custom Metrics',
        url: generateUrl(baseRoute, {
          initiativeId,
          groupId: CustomMetricsViewMode.Dashboard,
          ...(isPortfolioTracker && { portfolioId: initiativeId }),
        }),
      },
    ];
    if (isNewGroupMode) {
      breadcrumbs.push({
        label: 'Create custom metric pack',
        url: '',
      });
    }
    if (isManageMode) {
      breadcrumbs.push({
        label: 'Manage custom metrics',
        url: '',
      });
    }

    if (groupId && !isNewGroupMode && !isDashboardMode && !isManageMode) {
      const url = generateUrl(baseRoute, {
        initiativeId,
        groupId,
        ...(isPortfolioTracker && { portfolioId: initiativeId }),
        view: ViewMode.AssignQuestions,
      });
      breadcrumbs.push({ label: currentMetricGroup?.groupName ?? '', url });
    }
    if (view === ViewMode.Import) {
      const url = generateUrl(ROUTES.CUSTOM_METRICS, {
        initiativeId,
        groupId,
        view: ViewMode.Import,
      });
      breadcrumbs.push({ label: 'Import or Export', url });
    }
    return (
      <>
        {surveyId ? (
          <div className='w-100 ml-3 mb-4'>
            <Button color='link' className='px-2 pt-2' onClick={goToSurvey}>
              <i className='fa fa-arrow-circle-left mr-2' />
              Go back to survey scope
            </Button>
          </div>
        ) : null}
        <DashboardRow>
          <AdminBreadcrumbs breadcrumbs={breadcrumbs} initiativeId={initiativeId} isPortfolioTracker={isPortfolioTracker}/>
        </DashboardRow>
      </>
    );
  };

  const renderView = () => {
    if (isManageMode) {
      return <CustomMetricManage initiativeId={initiativeId} metricConfig={metricConfig} handleReload={handleReload} />;
    }
    if (isDashboardMode) {
      return <CustomMetricDashboard handleReload={handleReload} />;
    }

    return (
      <MetricGroupFormContainer
        isAssigned={isAssigned}
        currentMetricGroup={currentMetricGroup}
        metricConfig={metricConfig}
        handleReload={handleReload}
        handleChangeMetricGroup={props.handleChangeMetricGroup}
      />
    );
  };
  return (
    <Dashboard className='custom-metric-container'>
      {showAlert()}
      {renderBreadcrumbs()}
      {renderView()}
    </Dashboard>
  );
};

export default CustomMetrics;
